import BadgeRounded from '@mui/icons-material/BadgeRounded';
import ForwardToInboxRounded from '@mui/icons-material/ForwardToInboxRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { PageHeader } from '../../../components/states';
import { keys, useSubscriptionNotice, type InvitationSend } from '../api';
import { plural, type SubscriptionNotice } from '../lib';
import { NoAccess } from '../ui';
import { InviteDialog, InvitationsTab, InviteSentDialog } from './InvitationsTab';
import { RolesTab } from './RolesTab';
import { StaffDialog } from './StaffDialogs';
import { TeamTab } from './TeamTab';

const TABS = ['team', 'invitations', 'roles'] as const;
type TabId = (typeof TABS)[number];

/** Staff & roles: team members, pending invitations and the role/permission editor. */
export function StaffPage() {
  const { can } = useAuth();
  const canView = can('staff.view');
  const canManage = can('staff.manage');
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as TabId | null;
  const allowed: readonly TabId[] = canView ? TABS : ['roles'];
  const tab: TabId = requested && allowed.includes(requested) ? requested : (allowed[0] ?? 'roles');
  const select = (id: TabId) => setParams(id === allowed[0] ? {} : { tab: id });
  const [adding, setAdding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [sent, setSent] = useState<InvitationSend | null>(null);

  const pending = useQuery({
    queryKey: [...keys.invitations, { page: 1, pageSize: 1, status: 'PENDING' }],
    queryFn: () =>
      unwrap(
        api.GET('/staff/invitations', {
          params: { query: { page: 1, pageSize: 1, status: 'PENDING' } },
        }),
      ),
    enabled: canView,
  });
  const pendingCount = pending.data?.page.total ?? 0;
  const seats = useSubscriptionNotice(canView).data?.users;
  const full = Boolean(seats && seats.limit !== null && seats.used >= seats.limit);
  const fullHint =
    'User limit reached. Deactivate someone, cancel an invitation or contact PackBytes.';

  if (!canView && !can('roles.manage')) {
    return (
      <>
        <PageHeader title="Staff & roles" />
        <NoAccess what="staff and roles" />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Staff & roles"
        subtitle="Who works in your shop and what each person can do."
        actions={
          canManage && (
            <>
              <Tooltip title={full ? fullHint : ''}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ForwardToInboxRounded />}
                    onClick={() => setInviting(true)}
                    disabled={full}
                  >
                    Invite
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={full ? fullHint : ''}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddAlt1Rounded />}
                    onClick={() => setAdding(true)}
                    disabled={full}
                  >
                    Add staff
                  </Button>
                </span>
              </Tooltip>
            </>
          )
        }
      />
      {seats && <SeatStrip users={seats} full={full} />}
      <Tabs
        value={tab}
        onChange={(_, v: TabId) => select(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider', minHeight: 44 }}
      >
        {canView && <Tab value="team" icon={<BadgeRounded />} iconPosition="start" label="Team" />}
        {canView && (
          <Tab
            value="invitations"
            icon={<ForwardToInboxRounded />}
            iconPosition="start"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                Invitations
                {pendingCount > 0 && (
                  <Box
                    component="span"
                    className="tabular"
                    aria-label={`${String(pendingCount)} pending`}
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-grid',
                      placeItems: 'center',
                      color: 'primary.contrastText',
                      bgcolor: 'primary.main',
                    }}
                  >
                    {pendingCount}
                  </Box>
                )}
              </Box>
            }
          />
        )}
        <Tab value="roles" icon={<KeyRounded />} iconPosition="start" label="Roles" />
      </Tabs>

      {tab === 'team' && <TeamTab onAdd={() => !full && setAdding(true)} />}
      {tab === 'invitations' && (
        <InvitationsTab onInvite={() => setInviting(true)} onSent={setSent} />
      )}
      {tab === 'roles' && <RolesTab />}

      {adding && <StaffDialog staff={null} onClose={() => setAdding(false)} />}
      {inviting && (
        <InviteDialog
          onClose={() => setInviting(false)}
          onSent={(r) => {
            setSent(r);
            select('invitations');
          }}
        />
      )}
      {sent && <InviteSentDialog result={sent} onClose={() => setSent(null)} />}
    </>
  );
}

/** "3 of 5 users" with a bar; at the limit, says how to make room. */
export function SeatStrip({ users, full }: { users: SubscriptionNotice['users']; full: boolean }) {
  if (users.limit === null)
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {plural(users.activeUsers, 'active user')} · no user limit on your plan
      </Typography>
    );
  const detail = `${plural(users.activeUsers, 'active user')}, ${plural(users.pendingInvitations, 'pending invitation')}`;
  if (full)
    return (
      <Alert severity="warning" sx={{ mb: 2.5 }}>
        <strong>
          {users.used} of {plural(users.limit, 'user')}
        </strong>{' '}
        ({detail}): your {users.onTrial ? 'trial' : 'plan'} is full. Deactivate someone or cancel an
        invitation to make room, or contact PackBytes to allow more users.
      </Alert>
    );
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2.5, maxWidth: 520 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
        className="tabular"
      >
        {users.used} of {plural(users.limit, 'user')}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (users.used / users.limit) * 100)}
        aria-label="Users used"
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {detail}
      </Typography>
    </Stack>
  );
}
